import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { listAssets, uploadAsset } from '@/services/assets';
import { AssetBinaryImage } from '@/components/domain/AssetBinaryPreview';
import type { Asset } from '@/types/api';

const ACCEPT_IMAGE = 'image/jpeg,image/png,image/webp,image/gif';
const ACCEPT_VIDEO = 'video/mp4,video/webm,video/quicktime';
const ACCEPT_ALL = `${ACCEPT_IMAGE},${ACCEPT_VIDEO}`;

const TABS = ['My Assets', 'Upload', 'Web URL'] as const;
type Tab = (typeof TABS)[number];

function fileMatchesType(file: File, allowed: 'image' | 'video') {
  return allowed === 'image' ? file.type.startsWith('image/') : file.type.startsWith('video/');
}

function blobMatchesType(blob: Blob, ct: string, allowed?: 'image' | 'video') {
  if (!allowed) return true;
  const mime = (blob.type || ct).split(';')[0].toLowerCase();
  return allowed === 'image' ? mime.startsWith('image/') : mime.startsWith('video/');
}

function nameFromUrl(url: string): string {
  try {
    const path = new URL(url).pathname;
    return path.split('/').filter(Boolean).pop() ?? 'asset';
  } catch {
    return 'asset';
  }
}

interface InsertAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (assetId: number) => void;
  onAssetCreated?: (asset: Asset) => void;
  allowedAssetType?: 'image' | 'video';
}

export default function InsertAssetModal({
  isOpen,
  onClose,
  onSelect,
  onAssetCreated,
  allowedAssetType,
}: InsertAssetModalProps) {
  const [tab, setTab] = useState<Tab>('My Assets');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [error, setError] = useState('');

  // Upload tab
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploading, setUploading] = useState(false);

  // URL tab
  const [webUrl, setWebUrl] = useState('');
  const [webLoading, setWebLoading] = useState(false);

  const fetchAssets = useCallback(() => {
    setLoadingAssets(true);
    setError('');
    listAssets(0, 200)
      .then((data) => setAssets(data.filter((a) => a.id != null)))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load assets'))
      .finally(() => setLoadingAssets(false));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(null);
    setSearch('');
    setError('');
    setTab('My Assets');
    fetchAssets();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, fetchAssets, onClose]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q ? assets.filter((a) => a.name.toLowerCase().includes(q)) : assets;
    if (allowedAssetType) list = list.filter((a) => a.asset_type === allowedAssetType);
    return list;
  }, [assets, search, allowedAssetType]);

  function handleConfirm() {
    if (selected != null && onSelect) onSelect(selected);
    onClose();
    setSelected(null);
  }

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!uploadFile) return;
    if (allowedAssetType && !fileMatchesType(uploadFile, allowedAssetType)) {
      setError(`Please select a ${allowedAssetType} file.`);
      return;
    }
    setUploading(true);
    setError('');
    try {
      const asset = await uploadAsset({ file: uploadFile, name: uploadName.trim() || undefined });
      if (asset.id != null) {
        onAssetCreated?.(asset);
        setAssets((prev) => [asset, ...prev.filter((a) => a.id !== asset.id)]);
        setSelected(asset.id);
        setTab('My Assets');
      }
      fetchAssets();
      setUploadFile(null);
      setUploadName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleWebUrl(e: React.FormEvent) {
    e.preventDefault();
    const url = webUrl.trim();
    if (!url) return;
    setWebLoading(true);
    setError('');
    try {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const ct = res.headers.get('content-type') ?? '';
      const blob = await res.blob();
      if (!blobMatchesType(blob, ct, allowedAssetType)) {
        throw new Error(
          allowedAssetType ? `URL isn't a ${allowedAssetType}.` : 'Unsupported media type.',
        );
      }
      const name = nameFromUrl(url);
      const file = new File([blob], name, { type: blob.type || ct.split(';')[0] });
      const asset = await uploadAsset({ file });
      if (asset.id != null) {
        onAssetCreated?.(asset);
        setAssets((prev) => [asset, ...prev.filter((a) => a.id !== asset.id)]);
        setSelected(asset.id);
        setTab('My Assets');
      }
      fetchAssets();
      setWebUrl('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not fetch URL');
    } finally {
      setWebLoading(false);
    }
  }

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-on-surface/30 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-4xl bg-surface-container-lowest rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-outline-variant/10">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10">
          <div className="flex items-center gap-6 flex-1">
            <h2 className="font-headline font-bold text-on-surface text-lg whitespace-nowrap">
              Insert Asset
              {allowedAssetType && (
                <span className="ml-2 text-sm font-normal text-on-surface-variant">
                  ({allowedAssetType} only)
                </span>
              )}
            </h2>
            {tab === 'My Assets' && (
              <div className="relative w-full max-w-xs">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-[18px]">
                  search
                </span>
                <input
                  className="w-full pl-9 pr-4 py-2 text-sm bg-surface-container-low border border-outline-variant/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-on-surface placeholder:text-on-surface-variant/50"
                  placeholder="Search assets…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="ml-4 w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-container text-on-surface-variant hover:text-on-surface transition-colors cursor-pointer"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-8 px-6 border-b border-outline-variant/10 bg-surface-container-low/60">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`py-4 text-sm font-bold transition-colors cursor-pointer ${
                tab === t
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-error-container text-on-error-container text-sm">
              {error}
            </div>
          )}

          {/* My Assets */}
          {tab === 'My Assets' &&
            (loadingAssets ? (
              <div className="flex justify-center py-16">
                <span className="material-symbols-outlined animate-spin text-primary text-4xl">
                  progress_activity
                </span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center">
                <span className="material-symbols-outlined text-5xl text-primary/30">
                  folder_open
                </span>
                <p className="mt-4 text-on-surface-variant text-sm">
                  {assets.length === 0 ? 'No assets yet.' : 'No matching assets.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filtered.map((asset) => (
                  <div
                    key={asset.id}
                    onClick={() => setSelected(asset.id!)}
                    className={`group relative aspect-square bg-surface-container rounded-xl overflow-hidden cursor-pointer transition-all ${
                      selected === asset.id
                        ? 'ring-2 ring-primary border-2 border-primary'
                        : 'border border-outline-variant/20 hover:border-primary/40'
                    }`}
                  >
                    {asset.asset_type === 'video' && !asset.thumbnail_url ? (
                      <div className="w-full h-full flex items-center justify-center bg-surface-container-high">
                        <span className="material-symbols-outlined text-on-surface-variant/50 text-3xl">
                          movie
                        </span>
                      </div>
                    ) : (
                      <div className="w-full h-full opacity-90 group-hover:opacity-100 transition-opacity">
                        <AssetBinaryImage
                          assetId={asset.id!}
                          endpoint={asset.thumbnail_url ? 'thumbnail' : 'content'}
                          remoteUrlHint={asset.thumbnail_url ?? asset.uri}
                          alt={asset.alt_text}
                          className="w-full h-full object-cover"
                          loadingClassName="w-full h-full flex items-center justify-center bg-surface-container-high"
                        />
                      </div>
                    )}
                    {/* Name overlay */}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-[9px] font-bold text-white truncate">{asset.name}</p>
                    </div>
                    {/* Selected check */}
                    {selected === asset.id && (
                      <div className="absolute top-1.5 right-1.5 bg-primary text-on-primary rounded-full p-0.5">
                        <span className="material-symbols-outlined text-[13px]">check</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}

          {/* Upload */}
          {tab === 'Upload' && (
            <form onSubmit={handleUpload} className="max-w-md space-y-4">
              <div>
                <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-2">
                  File
                </label>
                <input
                  type="file"
                  accept={
                    allowedAssetType === 'image'
                      ? ACCEPT_IMAGE
                      : allowedAssetType === 'video'
                        ? ACCEPT_VIDEO
                        : ACCEPT_ALL
                  }
                  onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-on-surface-variant file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-container file:text-primary file:font-bold file:cursor-pointer hover:file:bg-primary-container/80"
                />
                {uploadFile && (
                  <p className="mt-1 text-xs text-on-surface-variant truncate">{uploadFile.name}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-2">
                  Display Name (optional)
                </label>
                <input
                  className="w-full px-3 py-2.5 bg-surface-container-low text-sm text-on-surface border border-outline-variant/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="My asset name"
                  value={uploadName}
                  onChange={(e) => setUploadName(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={!uploadFile || uploading}
                className="w-full py-3 bg-primary text-on-primary rounded-xl font-bold text-sm disabled:opacity-50 cursor-pointer hover:bg-primary-dim transition-all"
              >
                {uploading ? 'Uploading…' : 'Upload & Insert'}
              </button>
            </form>
          )}

          {/* Web URL */}
          {tab === 'Web URL' && (
            <form onSubmit={handleWebUrl} className="max-w-md space-y-4">
              <p className="text-xs text-on-surface-variant">
                Paste a public URL to an image or video. It will be fetched and saved to your asset
                library.
              </p>
              <div>
                <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-2">
                  URL
                </label>
                <input
                  type="url"
                  required
                  className="w-full px-3 py-2.5 bg-surface-container-low text-sm text-on-surface border border-outline-variant/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="https://example.com/image.jpg"
                  value={webUrl}
                  onChange={(e) => setWebUrl(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={!webUrl.trim() || webLoading}
                className="w-full py-3 bg-primary text-on-primary rounded-xl font-bold text-sm disabled:opacity-50 cursor-pointer hover:bg-primary-dim transition-all"
              >
                {webLoading ? 'Fetching…' : 'Add from URL'}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-outline-variant/10 flex items-center justify-between bg-surface-container-low/40">
          <p className="text-xs text-on-surface-variant">
            {tab === 'My Assets'
              ? `${filtered.length} asset${filtered.length !== 1 ? 's' : ''}`
              : '\u00a0'}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-bold text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selected == null || tab !== 'My Assets'}
              className="px-5 py-2 rounded-xl bg-primary text-on-primary text-sm font-bold disabled:opacity-40 cursor-pointer hover:bg-primary-dim transition-all"
            >
              Select Asset
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
